import { notifyError } from "common/utils/app-monitoring/notify";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageUploadError } from "common/utils/rudderstack/tracker";
import { shouldReportError } from "common/utils/request/request";
import { emitTransferUploadParallelEvent } from "common/utils/transfer-upload/parallel-emitter";
import { getUploadUuid } from "common/utils/transfer-upload/upload-uuid-tracker";

const dropZoneUnsupportedFileType = response => response === "You can't upload files of this type.";

const dropZoneNetworkError = response => response === "Server responded with 0 code.";

const axiosNetworkError = error => error.name === "AxiosError" && error.message === "Network Error";

const fetchErrors = response => ["Load failed", "Failed to fetch"].includes(response?.message);

export const RESOLUTION_GATE_EVENT = "image_upload_rejected_validation";

const getUploadErrorEventName = errorType => {
  const errorMap = {
    backend_file_validation: "image_validation_error",
    image_processing_error: "image_upload_process_error",
    current_pdp_date_json_error: "image_upload_process_error",
    product_prices_json_error: "image_upload_process_error",
    resolution_gate: RESOLUTION_GATE_EVENT
  };

  return errorMap[errorType] || "image_upload_error";
};

const generateUniqueId = () =>
  Math.random().toString(36).substring(2) + new Date().getTime().toString(36);

const EXPECTED_HTTP_STATUSES = [401, 403, 404, 422, 502, 503, 504];

const TRANSIENT_UPLOAD_PATTERNS = [
  /network\s*error/i,
  /timeout exceeded/i,
  /timeout of \d+ms exceeded/i,
  /ETIMEDOUT/i,
  /ERR_NETWORK/i,
  /ECONNABORTED/i,
  /ECONNRESET/i,
  /Failed to fetch/i,
  /Load failed/i,
  /canceled/i,
  /AbortError/i,
  /request\s*aborted/i,
  /status code 499/i,
  /status code 50[234]/i
];

const isTransientUploadError = error => {
  const message = typeof error === "string" ? error : error?.message || "";
  return TRANSIENT_UPLOAD_PATTERNS.some(pattern => pattern.test(message));
};

// Narrowly matches axios's canonical timeout shape. A broader `/timeout exceeded/i`
// fallback would misclassify server-echoed or application-level messages (e.g.
// a "Session timeout exceeded" string) as axios upload timeouts.
const UPLOAD_TIMEOUT_MESSAGE_PATTERN = /timeout of \d+ms exceeded/i;

const isUploadTimeoutError = error => {
  if (error?.code === "ECONNABORTED" || error?.code === "ERR_TIMEOUT") return true;
  const message = typeof error === "string" ? error : error?.message || "";
  return UPLOAD_TIMEOUT_MESSAGE_PATTERN.test(message);
};

const notifyUploadError = (name, error, logId) =>
  notifyError(name, {
    params: {
      error: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        code: error?.code
      },
      logId
    }
  });

export const logImageUploadError = error => {
  const logId = generateUniqueId();

  const status = error?.status || error?.response?.status;
  if (status && EXPECTED_HTTP_STATUSES.includes(status)) return logId;

  // Image upload timeouts are actionable (backend unresponsiveness) — report
  // with a distinct name so Honeybadger groups them separately, and bypass
  // the shared gates that otherwise suppress all timeouts.
  if (isUploadTimeoutError(error)) {
    notifyUploadError("Image upload timeout", error, logId);
    return logId;
  }

  if (!shouldReportError(error)) return logId;
  if (isTransientUploadError(error)) return logId;

  notifyUploadError("Image upload error", error, logId);
  return logId;
};

export const getErrorType = response => {
  let errorType;
  if (dropZoneUnsupportedFileType(response)) {
    errorType = "file type is not allowed";
  } else if (fetchErrors(response)) {
    errorType = response?.name;
  } else {
    errorType = response?.error_type || "unhandled_error";
  }

  return errorType;
};

/* eslint-disable max-lines-per-function */
export const getErrorContext = (pageInfo, file, error) => {
  let response;
  let errorMessageValue;
  let errorUUID;
  let errorDetails;
  let httpStatusCode;
  const dpiValue = 300;

  if (error.response) {
    response = error.response.data;
    httpStatusCode = error.response?.status;

    if (typeof response === "string") {
      errorMessageValue = response;
    } else {
      errorMessageValue = response.error;
      errorDetails = response.error_details;
      errorUUID = response.error_uuid;
    }
  } else {
    response = error;
    errorMessageValue = error?.message || error;
    httpStatusCode = error?.status || error?.code;
  }

  const errorTypeValue = getErrorType(response);

  if (
    errorTypeValue === "unhandled_error" &&
    errorMessageValue &&
    typeof errorMessageValue === "string" &&
    (errorMessageValue.toLowerCase().includes("the request could not be satisfied") ||
      errorMessageValue.toLowerCase().includes("generated by cloudfront"))
  ) {
    // This is a normalized message set to the 'error_message' property;
    // the actual error message is still present in the 'response' property
    errorMessageValue = "Cloudfront Bounce Error";
  }

  let rawError;
  try {
    rawError = String(error);
  } catch (e) {
    rawError = "[Error could not be stringified]";
  }

  return {
    error_type: errorTypeValue,
    error_message: errorMessageValue,
    progress_message: error?.progressMessage,
    error_uuid: errorUUID,
    error_status: errorDetails?.status,
    error_stderr: errorDetails?.stderr,
    http_status_code: httpStatusCode,
    raw_error: rawError,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    width: (file.width / dpiValue).toFixed(2),
    height: (file.height / dpiValue).toFixed(2),
    resolution_width: file.width,
    resolution_height: file.height,
    dpi: dpiValue,
    page: pageInfo.pageValue,
    business_line: pageInfo.businessLine || null, // MN-4800: nullable; gate (isTransferBusinessLine) rejects null.
    pdf_page_count: errorDetails?.pdf_page_count,
    project_id: null,
    response,
    upload_uuid: getUploadUuid(file), // MN-4800: WeakMap-backed lookup; falls back to legacy file.uploadUuid.
    ...(pageInfo.orderType && { order_type: pageInfo.orderType })
  };
};
/* eslint-enable max-lines-per-function */

export const logUploadErrorEvent = (pageInfo, file, error) => {
  let errorEvent;
  const response = error.response ? error.response.data : error;

  // TODO:
  // Need to organize different type of image upload errors better
  if (dropZoneUnsupportedFileType(response)) {
    errorEvent = "image_upload_unsupported_file";
  } else if (dropZoneNetworkError(response) || axiosNetworkError(error)) {
    errorEvent = "image_upload_network_error";
  } else if (fetchErrors(response)) {
    errorEvent = "image_fetch_error";
  } else {
    errorEvent = getUploadErrorEventName(response.error_type);
  }

  const errorContext = getErrorContext(pageInfo, file, error);
  if (errorEvent === "image_upload_error" && errorContext.error_uuid == null) {
    errorContext.log_id = logImageUploadError(error);
  }

  submitDtfEvent(errorEvent, errorContext);
  // MN-4800 dual-emission. Helper handles the business-line gate.
  emitTransferUploadParallelEvent({ legacyEvent: errorEvent, errorContext, file, pageInfo });

  // Resolution-gate (and other client-side validation) rejections are
  // user-correctable outcomes, not upload errors. Skip the Rudderstack
  // "Image Upload Error" event so they don't inflate the error-rate
  // dashboard — the distinct Mixpanel event above still tracks them.
  if (errorEvent === RESOLUTION_GATE_EVENT) return;

  trackRudderstackImageUploadError({
    error_message: errorEvent,
    file_name: errorContext.file_name,
    file_type: errorContext.file_type,
    file_size: errorContext.file_size,
    dpi: errorContext.dpi,
    business_line: pageInfo.businessLine,
    location: pageInfo.pageValue
  });
};

export const getUploadErrorMessage = response => {
  const responseError = typeof response === "string" ? response : response.error;
  return responseError || "Something went wrong. Please, try again";
};
